import { observer } from "mobx-react-lite";
import { Container, Grid, Header, Table } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useStore } from "../../../app/stores/store";
import PageHeader from "../../../app/layout/PageHeader";
import { formatCurrency } from "../../../app/common/util/functions";
import React from "react";

interface Props {
  startDate: Date;
  endDate: Date;
}

export default observer(function CoreTrackingPrint({
  startDate,
  endDate
}: Props) {
  const { t } = useTranslation(["common", "reports"]);

  const { coreTrackingReportStore } = useStore();
  const {
    groupedCoreTrackingReport,
    getCoreTrackingReport,
    calculateTotalQuantity,
    calculateTotalReturned,
    calculateTotalRefunded,
  } = coreTrackingReportStore;

  return (
    <>
      <Container className="page">
        <Grid stackable columns={2} className="headerPrint">
          <Grid.Row>
            <Grid.Column>
              <PageHeader
                header={`${t("report", { ns: "reports" })}: ${t(
                  "labels.coreTracking",
                  {
                    ns: "inventory",
                  }
                )}`}
                type="h2"
                divider={false}
                getAlerts={true}
                addTitle={true}
              />
            </Grid.Column>
            <Grid.Column>
              <Header
                content={`${format(startDate, "PP")} - ${format(
                  endDate,
                  "PP"
                )}`}
                as="h2"
                textAlign="right"
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {groupedCoreTrackingReport &&
          groupedCoreTrackingReport.length > 0 && (
            <Table striped unstackable className="dnxTable">
              <Table.Body>
                {groupedCoreTrackingReport.map(
                  ([group, items], groupIndex) => (
                    <React.Fragment key={groupIndex}>
                      <Table.Row>
                        <Table.Cell
                          className="reportTableHeader terms_bold"
                          singleLine
                        >
                          {group}
                        </Table.Cell>
                        <Table.Cell></Table.Cell>
                        <Table.Cell></Table.Cell>
                        <Table.Cell></Table.Cell>
                        <Table.Cell></Table.Cell>
                        <Table.Cell></Table.Cell>
                        <Table.Cell></Table.Cell>
                        <Table.Cell></Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell textAlign="left">
                          {t("labels.invoice_date", { ns: "inventory" })}
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                          {t("invoicenumber", { ns: "common" })}
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                          {t("part_number", { ns: "reports" })}
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                          {t("pops.part_desc", { ns: "tickets" })}
                        </Table.HeaderCell>
                        <Table.HeaderCell textAlign="right">
                          {t("coreValue", { ns: "inventory" })}
                        </Table.HeaderCell>
                        <Table.HeaderCell textAlign="right">{t("labels.totalQuantity", { ns: "inventory" })}</Table.HeaderCell>
                        <Table.HeaderCell textAlign="right">{t("returnedQty", { ns: "inventory" })}</Table.HeaderCell>
                        <Table.HeaderCell textAlign="right">{t("refundedQty", { ns: "inventory" })}</Table.HeaderCell>
                      </Table.Row>
                      {items.map((item, itemIndex) => (
                        <Table.Row key={item.id}>
                          <Table.Cell
                            data-label={t("labels.invoice_date", { ns: "inventory" })}
                            textAlign="left"
                          >
                            {item.invoiceDate ? format(new Date(item.invoiceDate), 'P') : null}
                          </Table.Cell>
                          <Table.Cell
                            className='hoveredLink'
                            onClick={() => window.open(`/ticket/view/${item.ticketId}`, `_`)}
                            data-label={t("invoicenumber", { ns: "common" })}
                            width={1}
                          >
                            #{item.invoiceNumber}
                          </Table.Cell>
                          <Table.Cell
                            // eslint-disable-next-line eqeqeq
                            className={item.inventoryStockId != undefined ? 'hoveredLink' : ''}
                            // eslint-disable-next-line eqeqeq
                            onClick={() => item.inventoryStockId != undefined ? window.open(`/inventoryIndex/partDetails/${item.partId}`, '_blank') : console.log('')}
                            data-label={t("part_number", { ns: "reports" })}
                            width={2}
                          >
                            {item.partNumber}
                          </Table.Cell>
                          <Table.Cell data-label={t("pops.part_desc", { ns: "tickets" })} width={3}>
                            {item.partDescription}
                          </Table.Cell>
                          <Table.Cell data-label={t("coreValue", { ns: "inventory" })} textAlign="right">
                            {formatCurrency(item.coreValue)}
                          </Table.Cell>
                          <Table.Cell data-label={t("labels.totalQuantity", { ns: "inventory" })} textAlign="right">
                            {item.quantity}
                          </Table.Cell>
                          <Table.Cell data-label={t("returnedQty", { ns: "inventory" })} textAlign="right">
                            {item.returnedQty}
                          </Table.Cell>
                          <Table.Cell data-label={t("refundedQty", { ns: "inventory" })} textAlign="right">
                            {item.refundedQty}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </React.Fragment>
                  )
                )}
                <Table.Row className="terms_bold greenRow">
                  <Table.Cell
                    data-label={t("labels.invoice_date", { ns: "inventory" })}
                    textAlign="left"
                    width={1}
                  ></Table.Cell>
                  <Table.Cell
                    data-label={t("invoicenumber", { ns: "common" })}
                    width={1}
                  ></Table.Cell>
                  <Table.Cell
                    data-label={t("part_number", { ns: "reports" })}
                    width={2}
                  ></Table.Cell>
                  <Table.Cell data-label={t("pops.part_desc", { ns: "tickets" })} width={3}>
                  </Table.Cell>
                  <Table.Cell data-label={t("coreValue", { ns: "inventory" })} textAlign="right">
                  </Table.Cell>
                  <Table.Cell data-label={t("labels.totalQuantity", { ns: "inventory" })} textAlign="right">
                    {calculateTotalQuantity(getCoreTrackingReport)}
                  </Table.Cell>
                  <Table.Cell data-label={t("returned", { ns: "inventory" })} textAlign="right">
                    {calculateTotalReturned(getCoreTrackingReport)}
                  </Table.Cell>
                  <Table.Cell data-label={t("refunded", { ns: "inventory" })} textAlign="right">
                    {calculateTotalRefunded(getCoreTrackingReport)}
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          )}
      </Container>
    </>
  );
});
